import { useState } from "react";
import { useCreateTopup } from "../hooks/useWallets";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";

interface Props {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TopupDialog = ({ customerId, open, onOpenChange }: Props) => {
  const notification = useNotification();
  const confirm = useConfirm();
  const mutation = useCreateTopup();
  const [amount, setAmount] = useState("");
  const [channel, setChannel] = useState<"Cash" | "MPesa" | "BankTransfer">("Cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  const reset = () => {
    setAmount("");
    setChannel("Cash");
    setReference("");
    setNotes("");
  };

  const submit = () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      notification.error("Amount must be greater than zero.");
      return;
    }
    const ref = reference.trim();
    if ((channel === "MPesa" || channel === "BankTransfer") && !ref) {
      notification.error("Reference is required for MPesa and Bank Transfer top-ups.");
      return;
    }
    if (channel === "MPesa" && !/^[A-Z0-9]{10}$/.test(ref)) {
      notification.error("Mpesa code must be 10 alphanumeric characters (uppercase).");
      return;
    }
    if (channel === "BankTransfer" && (ref.length < 4 || ref.length > 30)) {
      notification.error("Bank reference must be 4–30 characters.");
      return;
    }

    confirm({
      title: "Record wallet top-up?",
      description: `A ${channel} top-up will be recorded and held for finance approval.`,
      confirmLabel: "Record top-up",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Amount</span>
            <span className="font-medium">KES {value.toLocaleString()}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Channel</span>
            <span className="font-medium">{channel}</span>
          </p>
        </div>
      ),
      onConfirm: () =>
        mutation
          .mutateAsync({
            customerId,
            amount: value,
            channel,
            // Cash: empty allowed — server fills a "CASH" sentinel.
            payoutReference: ref || null,
            notes: notes.trim() || null,
          })
          .then(() => {
            notification.success(
              "Top-up recorded. Funds become spendable once finance approves."
            );
            reset();
            onOpenChange(false);
          })
          .catch((e) => {
            notification.error("Failed to record top-up.");
            throw e;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Top up wallet</DialogTitle>
          <DialogDescription>
            Standalone deposit (not tied to an order). Pending finance approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Field>
            <FieldLabel>Amount (KES) <span className="text-destructive">*</span></FieldLabel>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={mutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Channel <span className="text-destructive">*</span></FieldLabel>
            <Select
              value={channel}
              onValueChange={(v) => setChannel(v as typeof channel)}
              disabled={mutation.isPending}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="MPesa">M-Pesa</SelectItem>
                <SelectItem value="BankTransfer">Bank transfer</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>
              Reference
              {channel !== "Cash" && <span className="text-destructive"> *</span>}
            </FieldLabel>
            <Input
              placeholder={
                channel === "MPesa"
                  ? "Mpesa code (10 chars, e.g. QWE1234567)"
                  : channel === "BankTransfer"
                  ? "Bank transaction ID"
                  : "Optional — leave blank for cash at till"
              }
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              disabled={mutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} disabled={mutation.isPending} />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Recording..." : "Record top-up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
